import { useCallback, useEffect, useId, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  apiErrorStatus,
  apiLoadErrorText,
  prcVersionArg,
  useGetPrcVersionQuery,
  useListPrcVersionsQuery,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { typo } from '@entities/fleet';
import { Notice, TabPanel, Tabs, TextButton, type TabItem } from '@widgets/FleetRegistry/ui';
import { usePrcAccess } from '../access';
import { NewVersionDrawer } from '../list/NewVersionDrawer';
import { PrcRoot } from '../ui';
import { BreakEvenTab } from '../tabs/BreakEvenTab';
import { DiffTab } from '../tabs/DiffTab';
import { LimitsTab } from '../tabs/LimitsTab';
import { ParamsTab } from '../tabs/ParamsTab';
import { PayrollTab } from '../tabs/PayrollTab';
import { PricesTab } from '../tabs/PricesTab';
import { ResultsTab } from '../tabs/ResultsTab';
import { VariantBar } from './VariantBar';
import { VersionHeader } from './VersionHeader';

type VersionTab = 'results' | 'diff' | 'prices' | 'payroll' | 'params' | 'limits' | 'breakeven';

const TABS: readonly TabItem<VersionTab>[] = [
  { id: 'results', label: 'Результаты' },
  { id: 'diff', label: 'Было → стало' },
  { id: 'prices', label: 'Цены' },
  { id: 'payroll', label: 'ФОТ механизаторов' },
  { id: 'params', label: 'Параметры' },
  { id: 'limits', label: 'Лимиты на ремонт' },
  { id: 'breakeven', label: 'Безубыточность' },
];

function parseTab(raw: string | null): VersionTab {
  return TABS.find((t) => t.id === raw)?.id ?? 'results';
}

const TabsBar = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Panel = styled.div`
  padding-top: ${({ theme }) => theme.spacing(5)};
  padding-bottom: ${({ theme }) => theme.spacing(10)};
`;

function PageSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загрузка версии ПРЦ">
      <SkeletonLine $width="30%" />
      <SkeletonLine $height={28} $width="55%" />
      <SkeletonLine $width="70%" />
      <SkeletonLine $height={64} />
      <SkeletonLine $height={240} />
    </div>
  );
}

/**
 * Версия ПРЦ: шапка с маршрутом утверждения и действиями, условия расчёта
 * (основной расчёт и варианты условий), вкладки расчёта. Вкладка, вариант,
 * год, модель и «с чем сравнить» — в адресе: ссылку можно переслать.
 */
export function PrcVersionPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const access = usePrcAccess();
  const uid = useId();
  const [dirty, setDirty] = useState(0);
  const [copying, setCopying] = useState(false);
  // Вариант условий — `cond` в адресе; нет — основной расчёт.
  const variantId = params.get('cond');
  const head = useGetPrcVersionQuery(prcVersionArg(id, undefined, variantId), {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });
  const { data: list } = useListPrcVersionsQuery();
  const versions = list?.items ?? [];
  const payload =
    head.data &&
    head.data.version.id === id &&
    (head.data.variant?.id ?? null) === (variantId ?? null)
      ? head.data
      : undefined;
  // Вариант удалили (другой вкладкой, коллегой) — ссылка ведёт в основной расчёт.
  const variantGone =
    variantId !== null && head.error !== undefined && apiErrorStatus(head.error) === 404;

  const tab = parseTab(params.get('tab'));
  const baseYear = payload?.version.params.base_year;
  const yearParam = Number(params.get('year'));
  const year =
    payload && payload.version.params.years.includes(yearParam) ? yearParam : (baseYear ?? 0);
  const modelParam = params.get('model');
  const variant: 1 | 2 = params.get('variant') === '1' ? 1 : 2;
  const against = params.get('against') ?? 'current';

  const setParam = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, val] of Object.entries(patch)) {
            if (val === null) next.delete(k);
            else next.set(k, val);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Несохранённые правки — предупредить при закрытии вкладки браузера.
  useEffect(() => {
    if (dirty === 0) return;
    const guard = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  const onDirtyChange = useCallback((n: number) => setDirty(n), []);

  function changeTab(next: VersionTab, extra: Record<string, string | null> = {}) {
    if (next === tab && Object.keys(extra).length === 0) return;
    if (
      dirty > 0 &&
      !window.confirm(
        `Перейти без сохранения? Правки в ${dirty} ${pluralizeRu(dirty, ['значении', 'значениях', 'значениях'])} пропадут.`,
      )
    ) {
      return;
    }
    setDirty(0);
    setParam({ tab: next === 'results' ? null : next, model: null, ...extra });
  }

  const onYear = (y: number) => setParam({ year: y === baseYear ? null : String(y) });
  const compare = () => changeTab('diff', { against: null });

  useEffect(() => {
    if (variantGone) setParam({ cond: null });
  }, [variantGone, setParam]);

  function selectVariant(next: string | null) {
    if ((next ?? null) === (variantId ?? null)) return;
    if (
      dirty > 0 &&
      !window.confirm(
        `Перейти к другим условиям без сохранения? Правки в ${dirty} ${pluralizeRu(dirty, ['значении', 'значениях', 'значениях'])} пропадут.`,
      )
    ) {
      return;
    }
    setDirty(0);
    setParam({ cond: next, model: null, against: null });
  }
  // Вкладки с формами — заново на других условиях: блоки у них другие, а
  // `updated_at` версии тот же.
  const scopeKey = variantId ?? 'main';

  if (!id) return null;

  if (!payload) {
    const notFound = !variantGone && head.error !== undefined && apiErrorStatus(head.error) === 404;
    return (
      <PrcRoot>
        {head.error !== undefined ? (
          <Notice $tone="danger" role="alert">
            <span className="text">
              {notFound
                ? typo('Версия не найдена — возможно, черновик удалили или ссылка устарела.')
                : apiLoadErrorText(
                    head.error,
                    'Не удалось открыть версию ПРЦ.',
                    'Нет доступа к ПРЦ Экономиста.',
                  )}
            </span>
            {notFound ? (
              <Link to="/agents/econ/prc">К списку версий</Link>
            ) : (
              <TextButton type="button" onClick={() => void head.refetch()}>
                Повторить
              </TextButton>
            )}
          </Notice>
        ) : (
          <PageSkeleton />
        )}
      </PrcRoot>
    );
  }

  return (
    <PrcRoot>
      <VersionHeader
        payload={payload}
        versions={versions}
        access={access}
        dirty={dirty}
        onCopy={() => setCopying(true)}
        onCompare={compare}
        onReread={() => void head.refetch()}
      />

      <VariantBar
        payload={payload}
        versions={versions}
        access={access}
        variantId={variantId}
        onSelect={selectVariant}
      />

      <TabsBar>
        <Tabs
          items={TABS}
          value={tab}
          onChange={(t) => changeTab(t)}
          idPrefix={uid}
          label="Разделы версии"
        />
      </TabsBar>

      <Panel>
        <TabPanel idPrefix={uid} id={tab}>
          {tab === 'results' && (
            <ResultsTab
              key={scopeKey}
              payload={payload}
              access={access}
              year={year}
              onYear={onYear}
              openModelId={modelParam}
              onOpenModel={(m) => setParam({ model: m })}
              onCloseModel={() => setParam({ model: null })}
              onBreakEven={(m) => changeTab('breakeven', { model: m })}
            />
          )}
          {tab === 'diff' && (
            <DiffTab
              key={scopeKey}
              payload={payload}
              versions={versions}
              year={year}
              onYear={onYear}
              against={against}
              onAgainst={(a) => setParam({ against: a === 'current' ? null : a })}
            />
          )}
          {tab === 'prices' && (
            <PricesTab
              key={scopeKey}
              payload={payload}
              access={access}
              onDirtyChange={onDirtyChange}
              onCompare={compare}
            />
          )}
          {tab === 'payroll' && (
            <PayrollTab
              key={scopeKey}
              payload={payload}
              access={access}
              onDirtyChange={onDirtyChange}
              onCompare={compare}
            />
          )}
          {tab === 'params' && (
            <ParamsTab
              key={scopeKey}
              payload={payload}
              access={access}
              onDirtyChange={onDirtyChange}
              onCompare={compare}
            />
          )}
          {tab === 'limits' && (
            <LimitsTab
              key={scopeKey}
              payload={payload}
              access={access}
              openModelId={modelParam}
              onOpenModel={(m) => setParam({ model: m })}
              onCloseModel={() => setParam({ model: null })}
            />
          )}
          {tab === 'breakeven' && (
            <BreakEvenTab
              key={scopeKey}
              payload={payload}
              modelId={modelParam}
              onModel={(m) => setParam({ model: m })}
              year={year}
              onYear={onYear}
              variant={variant}
              onVariant={(vv) => setParam({ variant: vv === 2 ? null : '1' })}
            />
          )}
        </TabPanel>
      </Panel>

      {copying && (
        <NewVersionDrawer
          versions={versions.length > 0 ? versions : []}
          baseVersionId={payload.version.id}
          onClose={() => setCopying(false)}
          onCreated={(newId) => {
            setCopying(false);
            navigate(`/agents/econ/prc/${newId}`);
          }}
        />
      )}
    </PrcRoot>
  );
}
